import { useState, useEffect } from "react";
import { api, setJwt } from "../api";
import type { Identity } from "../types";
import wordmark from "../assets/brand/wordmark-light.svg";

type Props = { onAuth: (identity: Identity) => void };

function emailInitials(email: string): string {
  const local = email.split("@")[0];
  const parts = local.split(/[._-]/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return local.slice(0, 2).toUpperCase();
}

export default function SignIn({ onAuth }: Props) {
  const [phase, setPhase] = useState<"default" | "sent" | "verifying" | "link_error">(() =>
    new URLSearchParams(window.location.search).get("token") ? "verifying" : "default"
  );
  const [email, setEmail] = useState("");
  const [sentTo, setSentTo] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get("token");
    if (!token) return;
    window.history.replaceState({}, "", window.location.pathname);
    api
      .verifyToken(token)
      .then((res) => {
        setJwt(res.jwt);
        onAuth(res.identity);
      })
      .catch(() => {
        setPhase("link_error");
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSend(e: React.FormEvent) {
    e.preventDefault();
    const addr = email.trim();
    if (!addr) return;
    setSending(true);
    setSendError(null);
    try {
      await api.requestMagicLink(addr);
      setSentTo(addr);
      setPhase("sent");
    } catch (err: unknown) {
      setSendError(err instanceof Error ? err.message : "Something went wrong. Try again.");
    } finally {
      setSending(false);
    }
  }

  if (phase === "verifying") {
    return (
      <div className="signin-stage">
        <div className="signin-card">
          <img className="wordmark" src={wordmark} alt="BgSPIM" />
          <p style={{ color: "rgba(240,230,207,0.75)", fontSize: 15 }}>Verifying link…</p>
        </div>
      </div>
    );
  }

  if (phase === "sent") {
    return (
      <div className="signin-stage">
        <div className="signin-card">
          <img className="wordmark" src={wordmark} alt="BgSPIM" />
          <h1>Check your email.</h1>
          <p className="sub">
            We sent a link to <strong>{sentTo}</strong>.
          </p>
          <p style={{ fontSize: 13, color: "rgba(240,230,207,0.55)" }}>
            The link expires in 30 minutes.{" "}
            <button
              style={{
                background: "none",
                border: "none",
                padding: 0,
                color: "rgba(240,230,207,0.55)",
                textDecoration: "underline",
                cursor: "pointer",
                fontSize: 13,
              }}
              onClick={() => setPhase("default")}
            >
              Send again
            </button>
          </p>
          <div className="footer-line">
            BgSPIM · BeargrassAI Service and Product Information Manager
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="signin-stage">
      <div className="signin-card">
        <img className="wordmark" src={wordmark} alt="BgSPIM" />

        {phase === "link_error" && (
          <div
            role="alert"
            style={{
              marginBottom: 20,
              padding: "10px 14px",
              background: "rgba(229,138,109,0.12)",
              border: "1px solid rgba(229,138,109,0.35)",
              borderRadius: "var(--r-sm)",
              color: "#e58a6d",
              fontSize: 13,
              fontFamily: "var(--font-mono)",
            }}
          >
            Link expired or invalid. Request a new one.
          </div>
        )}

        <h1>Sign in.</h1>
        <p className="sub">Enter your email and we'll send you a magic link.</p>

        <form onSubmit={(e) => void handleSend(e)}>
          <div style={{ marginBottom: 18 }}>
            <label
              htmlFor="signin-email"
              style={{
                display: "block",
                fontSize: 11,
                fontFamily: "var(--font-mono)",
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                color: "var(--cream-fixed)",
                marginBottom: 6,
              }}
            >
              Email
            </label>
            <input
              id="signin-email"
              className="input"
              type="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
            />
          </div>

          {sendError && (
            <div
              role="alert"
              style={{
                marginBottom: 14,
                fontSize: 13,
                fontFamily: "var(--font-mono)",
                color: "#e58a6d",
              }}
            >
              {sendError}
            </div>
          )}

          <button
            type="submit"
            className="btn btn-warm btn-lg"
            style={{ width: "100%" }}
            disabled={sending}
          >
            {sending ? "Sending…" : "Send me a link"}
          </button>
        </form>

        <div className="footer-line">
          BgSPIM · BeargrassAI Service and Product Information Manager
        </div>
      </div>
    </div>
  );
}

